'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  region: string;
  image: string;
  alt: string;
  quote: string;
  impact: string;
}

interface RegionalTestimonialsProps {
  testimonials: Testimonial[];
  className?: string;
}

const RegionalTestimonials = ({
  testimonials,
  className = '',
}: RegionalTestimonialsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>('All');

  useState(() => {
    setIsHydrated(true);
  });

  const regions = ['All', 'India', 'UAE', 'International'];

  const filteredTestimonials =
    selectedRegion === 'All'
      ? testimonials
      : testimonials.filter((t) => t.region === selectedRegion);

  return (
    <section className={`bg-background py-16 md:py-20 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-conversion/10 px-4 py-2 rounded-full mb-4">
            <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-conversion" />
            <span className="text-conversion font-medium text-sm">
              Client Success Stories
            </span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Trusted Across Regions
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Hear from organizations that have transformed their sustainability journey with our solutions
          </p>
        </div>

        {/* Region Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {regions.map((region) => (
            <button
              key={region}
              onClick={() => isHydrated && setSelectedRegion(region)}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-300 ${
                selectedRegion === region
                  ? 'bg-primary text-primary-foreground shadow-natural'
                  : 'bg-surface text-text-secondary hover:bg-muted'
              }`}
            >
              {region}
            </button>
          ))}
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTestimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-card rounded-2xl shadow-natural hover:shadow-natural-lg transition-all duration-300 overflow-hidden border border-border"
            >
              <div className="p-6 md:p-8">
                {/* Quote Icon */}
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl mb-6">
                  <Icon
                    name="ChatBubbleBottomCenterTextIcon"
                    size={24}
                    className="text-primary"
                    variant="solid"
                  />
                </div>

                {/* Quote */}
                <p className="text-text-primary leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>

                {/* Impact Badge */}
                <div className="bg-conversion/10 rounded-lg p-4 mb-6">
                  <div className="text-conversion text-sm font-semibold mb-1">
                    Measurable Impact
                  </div>
                  <div className="text-text-primary font-medium">
                    {testimonial.impact}
                  </div>
                </div>

                {/* Author Info */}
                <div className="flex items-center space-x-4">
                  <div className="relative w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                      src={testimonial.image}
                      alt={testimonial.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-text-primary truncate">
                      {testimonial.name}
                    </div>
                    <div className="text-text-secondary text-sm truncate">
                      {testimonial.role}
                    </div>
                    <div className="text-text-secondary text-sm truncate">
                      {testimonial.company}
                    </div>
                    <div className="inline-block bg-trust/10 text-trust text-xs px-2 py-1 rounded-full mt-1 font-medium">
                      {testimonial.region}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RegionalTestimonials;